/**
 * Image Loading Utilities
 *
 * Shared utilities for loading images in canvas-based components.
 * Used by AchievementShareCard, BadgeShareModal, ShareProgressModal.
 */

/**
 * Load an image and return a promise
 * @param {string} src - Image source URL
 * @returns {Promise<HTMLImageElement>} Loaded image element
 */
import { createLogger } from './logger'

const logger = createLogger('imageLoading')

export const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      // Clean up event handlers to prevent memory leaks
      img.onload = null
      img.onerror = null
      resolve(img)
    }
    img.onerror = () => {
      // Clean up event handlers to prevent memory leaks
      img.onload = null
      img.onerror = null
      reject(new Error(`Failed to load image: ${src}`))
    }
    img.src = src
  })
}

/**
 * Load multiple badge images, returning loaded images or null for failures.
 * Logs warnings for failed loads to help diagnose CORS or path issues.
 *
 * @param {Array<{iconPath?: string}>} badges - Array of badge objects with iconPath
 * @returns {Promise<Array<HTMLImageElement|null>>} Array of loaded images or null for failures
 */
export const loadBadgeImages = async (badges) => {
  const results = await Promise.allSettled(
    badges.map((badge) => (badge.iconPath ? loadImage(badge.iconPath) : Promise.resolve(null)))
  )
  return results.map((result, index) => {
    if (result.status === 'rejected') {
      logger.warn(`Failed to load badge image ${badges[index]?.iconPath}:`, result.reason?.message)
    }
    return result.status === 'fulfilled' ? result.value : null
  })
}

/**
 * Rarity color mappings for badges
 */
export const RARITY_COLORS = {
  COMMON: { bg: '#6b7280', text: '#f3f4f6' },
  UNCOMMON: { bg: '#22c55e', text: '#ffffff' },
  RARE: { bg: '#3b82f6', text: '#ffffff' },
  EPIC: { bg: '#8b5cf6', text: '#ffffff' },
  LEGENDARY: { bg: '#f59e0b', text: '#ffffff' }
}

/**
 * Rarity gradient mappings for badges
 */
export const RARITY_GRADIENTS = {
  COMMON: ['#6b7280', '#4b5563'],
  UNCOMMON: ['#22c55e', '#16a34a'],
  RARE: ['#3b82f6', '#2563eb'],
  EPIC: ['#8b5cf6', '#7c3aed'],
  LEGENDARY: ['#f59e0b', '#d97706']
}
